import {
  DefaultFetchError,
  FetchCreate, FetchDelete,
  FetchGet,
  FetchGetId,
  FetchSuccess, FetchUpdate,
  PagingDataResponse,
  PagingParams,
  useFetchCreate, useFetchDelete,
  useFetchGet,
  useFetchGetId,
  useFetchUpdate,
} from '../fetch';
import { User, UserStatusType } from '../../store/auth';

type UserEntityType = 'users' | 'admins';

interface AdministratorsGetParams extends PagingParams {
  orderByColumn?: 'username' | 'firstName';
}

export const useUsersGet = <DD = UsersTableData>(
  decorateData?: (data: PagingDataResponse<User>) => DD,
  type: UserEntityType = 'users',
): FetchGet<
  PagingDataResponse<User>,
  AdministratorsGetParams,
  DefaultFetchError,
  DD
> => useFetchGet(
    type,
    {
      autoStart: false,
      startStateLoading: false,
      decorateData,
    },
  );

export interface UserTableRow extends Omit<User, 'settings'> {
  key: string;
  fullName: string;
}

export interface UsersTableData {
  data: UserTableRow[];
  total: number;
}

export const useUsersTableGet = (type: UserEntityType = 'users') => useUsersGet<UsersTableData>(
  ({ data, meta }: PagingDataResponse<User>): UsersTableData => ({
    data: data.map(({
      settings, ...item
    }: User): UserTableRow => ({
      key: item.id,
      fullName: `${item.firstName || '-'}`,
      ...item,
    })),
    total: meta.itemCount,
  }),
  type,
);

export const useUserGetById = <DD = User>(
  id?: string,
  decorateData?: (data: User) => DD,
  type: UserEntityType = 'users',
): FetchGetId<
  User,
  DefaultFetchError,
  unknown,
  DD
> => useFetchGetId(
    type,
    id,
    {
      autoStart: !!id,
      decorateData,
    },
  );

export interface UserCreateParams extends Pick<User, | 'role' | 'email'> { // 'firstName' | 'phone' | 'dateOfBirth'
  firstName?: User['firstName'],
  lastName?: User['lastName'],
  status?: UserStatusType;
}

export const useUserCreate = (type: UserEntityType = 'users')
  : FetchCreate<User, DefaultFetchError, UserCreateParams> => (
  useFetchCreate(type)
);

export type UserUpdateParams = Partial<UserCreateParams>

export const useUserUpdate = <
  TParams = UserUpdateParams
>(
    id?: string,
    type: UserEntityType = 'users',
  ): FetchUpdate<User, DefaultFetchError, TParams> => useFetchUpdate<User, DefaultFetchError, TParams>(type, id);
/* export const useUserUpdate = (id?: string, type: UserEntityType = 'users'): FetchUpdate<
  User, DefaultFetchError, UserUpdateParams> => ( // FetchSuccess
  useFetchUpdate(type, id)
); */

export const useUserChangeEmails = (id?: string): FetchUpdate<
  FetchSuccess, DefaultFetchError, Pick<UserUpdateParams, 'email'>> => (
  useFetchUpdate('users', id ? `${id}/change-email` : '')
);

export const useChangeUserStatus = (id?: string): FetchUpdate<
  User, DefaultFetchError, Pick<UserUpdateParams, 'status'>> => (
  useFetchUpdate('users', id)
);

// TODO mb remove, looks like not needed:
export interface UserSendInvitationParams {
  fullName: string;
  email: string;
}

export const useUserSendInvitation = (id?: string): FetchUpdate<
  FetchSuccess, DefaultFetchError, UserSendInvitationParams
> => (
  useFetchUpdate(id ? `users/${id}/invitation` : 'users')
);

export const useUserResetPassword = (id?: string): FetchGetId<User> => (
  useFetchGetId('users', id, { autoStart: false }));

export const useUserForgotPassword = (): FetchCreate<FetchSuccess, DefaultFetchError, { email: string }> => (
  useFetchCreate('auth/forgot-password')
);

export const useUserDelete = (id?: string, type: UserEntityType = 'users'): FetchDelete<
  FetchSuccess, DefaultFetchError
> => (
  useFetchDelete(type, id)
);

export interface UserChangePasswordParams {
  currentPassword: string;
  newPassword: string;
}

export const useUserChangePassword = (): FetchCreate<
  FetchSuccess, DefaultFetchError, UserChangePasswordParams> => (
  useFetchUpdate('users/update-password')
);

// TODO remove?:
/** Client user */
// export interface UserCreateParams extends Partial<AdministratorCreateParams> {
//   isCompanyAccepted: boolean;
//   isTermsOfServiceAccepted: boolean;
// }
//
// export const useUpdateMe = (): FetchUpdate<
//   User, DefaultFetchError, Partial<UserCreateParams>> => (
//   useFetchUpdate('users/me')
// );

/** CSV */
export const useUsersExportCSV = (): FetchGetId<string, DefaultFetchError> => (
  // useFetchCreate('users/export', { autoStart: false })
  useFetchGetId('users/export', '', { autoStart: false })
);
